<template>
  <div
    class="tab-pane fade"
    :class="{ 'show active': isActive }"
    id="nav-event-highlights"
    role="tabpanel"
    aria-labelledby="nav-event-highlights-tab"
  >
    <div class="event-highlights">
      <ul
        id="highlights-pills-tab"
        class="nav nav-pills highlights-tabs"
        role="tablist"
      >
        <li class="nav-item">
          <a
            id="highlights-tab"
            class="nav-link active"
            data-toggle="pill"
            href="#highlights-pane"
            role="tab"
            aria-controls="highlights-pane"
            aria-selected="true"
          >
            Highlights ({{ highlights.length }})
          </a>
        </li>
        <li class="nav-item">
          <a
            id="purchased-tab"
            class="nav-link"
            data-toggle="pill"
            href="#purchased-pane"
            role="tab"
            aria-controls="purchased-pane"
            aria-selected="false"
          >
            Purchased ({{ purchased.length }})
          </a>
        </li>
      </ul>

      <div class="tab-content highlights-tab-content">
        <div
          id="highlights-pane"
          class="tab-pane fade show active"
          role="tabpanel"
          aria-labelledby="highlights-tab"
        >
          <p class="highlights-note">
            Note: Each video can be downloaded within 30 days of the activity
            upload date. Once purchased and downloaded, they remain in your
            library for 30 days from the purchase date.
          </p>
          <div class="row" v-if="highlights.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of highlights"
              :key="i"
            >
              <highlight-card
                :video="video"
                @play="openPlayer"
                @download="confirmDownload"
              />
            </div>
          </div>
          <p v-else class="empty-state">No highlights available.</p>
          <div v-if="highlightsHasMore" class="hl-load-more">
            <button
              type="button"
              class="hl-btn hl-btn--cancel"
              :disabled="isLoadingMoreHighlights"
              @click="loadMoreHighlights"
            >
              <span>{{ isLoadingMoreHighlights ? "Loading…" : "Load More" }}</span>
            </button>
          </div>
        </div>

        <div
          id="purchased-pane"
          class="tab-pane fade"
          role="tabpanel"
          aria-labelledby="purchased-tab"
        >
          <p class="highlights-note">
            Note: Each purchased video remains in your library for 30 days from
            the purchase date.
          </p>
          <div class="row" v-if="purchased.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of purchased"
              :key="i"
            >
              <highlight-card
                :video="video"
                purchased
                @play="openPlayer"
                @download="confirmRedownload"
                @delete="confirmDelete"
              />
            </div>
          </div>
          <p v-else class="empty-state">No purchased videos.</p>
          <div v-if="purchasedHasMore" class="hl-load-more">
            <button
              type="button"
              class="hl-btn hl-btn--cancel"
              :disabled="isLoadingMorePurchased"
              @click="loadMorePurchased"
            >
              <span>{{ isLoadingMorePurchased ? "Loading…" : "Load More" }}</span>
            </button>
          </div>
        </div>
      </div>

      <highlight-video-player
        v-if="activeVideo"
        :video="activeVideo"
        @close="activeVideo = null"
        @download="confirmDownload"
      />

      <highlight-dialog
        v-if="confirmVideo"
        title="Download Video"
        @close="confirmVideo = null"
      >
        <p class="hl-confirm-text">
          Downloading this video will cost {{ confirmVideo.points }} Gems.<br />
          Do you want to continue?
        </p>
        <div class="hl-confirm-box">
          <div class="hl-confirm-cost">
            <img src="/assets/event-highlights/gems.svg" alt="" />
            <span>{{ confirmVideo.points }}</span>
          </div>
          <div v-if="canAfford" class="hl-confirm-balance">
            Gems Balance: {{ gems }}
          </div>
          <div v-else class="hl-confirm-balance is-error">
            <i class="fas fa-exclamation-circle"></i>
            Insufficient Gems Balance: {{ gems }}
          </div>
        </div>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="confirmVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            v-if="canAfford"
            type="button"
            class="hl-btn hl-btn--download"
            @click="doDownload"
          >
            <span>Download</span>
          </button>
          <button
            v-else
            type="button"
            class="hl-btn hl-btn--buygems"
            @click="buyGems"
          >
            <span>Buy Gems</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="redownloadVideo"
        title="Download Video"
        @close="redownloadVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to download this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="redownloadVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            type="button"
            class="hl-btn hl-btn--download"
            @click="doRedownload"
          >
            <span>Download</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="deleteVideo"
        title="Delete Video"
        @close="deleteVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to delete this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="deleteVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button type="button" class="hl-btn hl-btn--danger" @click="doDelete">
            <span>Delete</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-download-dialog
        v-if="downloadingVideo"
        :video="downloadingVideo"
        :progress="downloadProgress"
        :downloaded-bytes="downloadedBytes"
        :total-bytes="totalBytes"
        :state="downloadDialogState"
        @close="closeDownloading"
      />
    </div>
  </div>
</template>

<script>
import HighlightCard from "./HighlightCard";
import HighlightDialog from "./HighlightDialog";
import HighlightDownloadDialog from "./HighlightDownloadDialog";
import HighlightVideoPlayer from "./HighlightVideoPlayer";
import {
  mapHighlight,
  mapPurchase,
  pageItems,
  pageHasMore,
  pageCurrent,
} from "@/utils/eventHighlights";
import "@/assets/css/event-highlights.css";

export default {
  name: "EventHighlights",
  components: {
    HighlightCard,
    HighlightDialog,
    HighlightDownloadDialog,
    HighlightVideoPlayer,
  },
  props: {
    isActive: { type: Boolean, default: false },
    // current player gems balance (from rider_default_data via PlayerActivities)
    gemsBalance: { type: Number, default: 0 },
  },
  emits: ["buy-gems"],
  data() {
    return {
      activeVideo: null,
      confirmVideo: null,
      redownloadVideo: null,
      deleteVideo: null,
      downloadingVideo: null,
      downloadProgress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      downloadError: false,
      // local mirror of the gemsBalance prop, decremented optimistically on
      // download until the backend (which deducts via Java) is wired up
      gems: this.gemsBalance,
      highlights: [],
      purchased: [],
      highlightsPage: 1,
      purchasedPage: 1,
      highlightsHasMore: false,
      purchasedHasMore: false,
      isLoadingMoreHighlights: false,
      isLoadingMorePurchased: false,
    };
  },
  computed: {
    canAfford() {
      if (!this.confirmVideo) return false;
      return this.gems >= this.confirmVideo.points;
    },
    overlayOpen() {
      return (
        !!this.activeVideo ||
        !!this.confirmVideo ||
        !!this.redownloadVideo ||
        !!this.deleteVideo ||
        !!this.downloadingVideo
      );
    },
    downloadDialogState() {
      if (this.downloadError) return "error";
      if (this.downloadProgress >= 100) return "complete";
      return "downloading";
    },
  },
  watch: {
    gemsBalance(val) {
      this.gems = val;
    },
    overlayOpen(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
  },
  async mounted() {
    await this.loadHighlights();
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
  methods: {
    async loadHighlights() {
      const [hl, pu] = await Promise.all([
        this.$store.dispatch("user/event_highlights", { page: 1 }),
        this.$store.dispatch("user/event_highlights_purchased", { page: 1 }),
      ]);
      this.highlights = pageItems(hl).map(mapHighlight);
      this.purchased = pageItems(pu).map(mapPurchase);
      this.highlightsPage = pageCurrent(hl);
      this.purchasedPage = pageCurrent(pu);
      this.highlightsHasMore = pageHasMore(hl);
      this.purchasedHasMore = pageHasMore(pu);
    },
    async loadMoreHighlights() {
      if (this.isLoadingMoreHighlights || !this.highlightsHasMore) return;
      this.isLoadingMoreHighlights = true;
      try {
        const page = await this.$store.dispatch("user/event_highlights", {
          page: this.highlightsPage + 1,
        });
        this.highlights.push(...pageItems(page).map(mapHighlight));
        this.highlightsPage = pageCurrent(page);
        this.highlightsHasMore = pageHasMore(page);
      } finally {
        this.isLoadingMoreHighlights = false;
      }
    },
    async loadMorePurchased() {
      if (this.isLoadingMorePurchased || !this.purchasedHasMore) return;
      this.isLoadingMorePurchased = true;
      try {
        const page = await this.$store.dispatch(
          "user/event_highlights_purchased",
          { page: this.purchasedPage + 1 }
        );
        this.purchased.push(...pageItems(page).map(mapPurchase));
        this.purchasedPage = pageCurrent(page);
        this.purchasedHasMore = pageHasMore(page);
      } finally {
        this.isLoadingMorePurchased = false;
      }
    },
    openPlayer(video) {
      this.activeVideo = video;
    },
    confirmDownload(video) {
      this.confirmVideo = video;
      // Pull the current gem balance from the server so the dialog isn't
      // showing a stale figure if the user spent/earned gems elsewhere in
      // this session. Fire-and-forget — the watcher on `gemsBalance` will
      // update `this.gems` as soon as Vuex commits the fresh data.
      this.$store.dispatch("user/get_rider_default_data");
    },
    async doDownload() {
      if (!this.confirmVideo) return;
      if (!this.canAfford) return;
      const video = this.confirmVideo;

      this.confirmVideo = null;
      this.activeVideo = null;

      // Record the purchase server-side so it survives reload. Action returns
      // { purchase, gems } where `gems` is the new balance after deduction,
      // or null for the idempotent path (already-purchased) where no
      // deduction happened.
      let result = null;
      try {
        result = await this.$store.dispatch(
          "user/event_highlights_download",
          video.highlightId
        );
      } catch (e) {
        // backend rejected (insufficient gems, expired, etc.) — bail out
        return;
      }

      // Only proceed if the backend actually confirmed the purchase row —
      // otherwise we'd "move" the card to Purchased without persistence and
      // it'd flip back on the next reload.
      const purchaseDoc = result?.purchase ?? null;
      if (!purchaseDoc) {
        return;
      }

      const newGems = typeof result?.gems === "number" ? result.gems : null;

      // Sync local gem balance with the authoritative server value when
      // available; otherwise fall back to the optimistic decrement.
      if (newGems !== null) {
        this.gems = newGems;
      } else {
        this.gems -= video.points;
      }

      const idx = this.highlights.indexOf(video);
      if (idx !== -1) this.highlights.splice(idx, 1);
      const purchasedVideo = mapPurchase(purchaseDoc);
      this.purchased.push(purchasedVideo);

      await this.startDownload(purchasedVideo);
    },
    confirmRedownload(video) {
      this.redownloadVideo = video;
    },
    async doRedownload() {
      if (!this.redownloadVideo) return;
      const video = this.redownloadVideo;
      this.redownloadVideo = null;
      await this.startDownload(video);
    },
    async startDownload(video) {
      if (!video || !video.downloadUrl || video.downloadUrl === "#") return;

      this.downloadingVideo = video;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.totalBytes = 0;
      this.downloadError = false;

      try {
        const resp = await fetch(video.downloadUrl);
        if (!resp.ok) throw new Error("HTTP " + resp.status);

        const total = Number(resp.headers.get("Content-Length")) || 0;
        this.totalBytes = total;
        const reader = resp.body && resp.body.getReader && resp.body.getReader();

        let blob;
        if (reader) {
          const chunks = [];
          let received = 0;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            received += value.length;
            this.downloadedBytes = received;
            if (total > 0) {
              this.downloadProgress = Math.min(
                99,
                Math.round((received / total) * 100)
              );
            }
          }
          blob = new Blob(chunks, {
            type: resp.headers.get("Content-Type") || "video/mp4",
          });
        } else {
          blob = await resp.blob();
          this.downloadedBytes = blob.size;
          if (!this.totalBytes) this.totalBytes = blob.size;
        }

        if (!this.totalBytes && this.downloadedBytes) {
          this.totalBytes = this.downloadedBytes;
        }
        this.downloadProgress = 100;
        this.saveBlob(blob, video);
      } catch (e) {
        // Surface the failure in the dialog so the user knows the download
        // didn't go through. The dialog stays open until they close it.
        this.downloadError = true;
      }
    },
    closeDownloading() {
      this.downloadingVideo = null;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.totalBytes = 0;
      this.downloadError = false;
    },
    saveBlob(blob, video) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (video.title || "highlight") + ".mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    buyGems() {
      this.confirmVideo = null;
      // let the parent (PlayerActivities) switch to the In Game Shop tab
      this.$emit("buy-gems");
    },
    confirmDelete(video) {
      this.deleteVideo = video;
    },
    async doDelete() {
      if (!this.deleteVideo) return;
      const video = this.deleteVideo;
      this.deleteVideo = null;

      if (video.purchaseId) {
        try {
          await this.$store.dispatch(
            "user/event_highlights_delete_purchase",
            video.purchaseId
          );
        } catch (e) {
          // backend delete failed — keep the card in Purchased so the user can retry
          return;
        }
      }

      // optimistic remove so the card leaves Purchased immediately
      const idx = this.purchased.indexOf(video);
      if (idx !== -1) this.purchased.splice(idx, 1);

      // Re-fetch both lists so the underlying highlight reappears in
      // Highlights (if still within the 30-day window) without a page reload.
      // The backend filter remains the source of truth for eligibility.
      await this.loadHighlights();
    },
  },
};
</script>

<style scoped>
/* standalone panel on the player-activities page (no longer wrapped by the
   edit-profile .login-box, so it brings its own surface + spacing) */
#nav-event-highlights {
  padding: 24px 0 40px;
}
.event-highlights {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
@media (max-width: 575.98px) {
  .event-highlights {
    padding: 16px;
  }
}
.highlights-tabs {
  background: #e8e8e8;
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 20px;
}
.highlights-tabs .nav-item {
  flex: 1 1 50%;
  text-align: center;
}
.highlights-tabs .nav-link {
  color: #1a1a1a;
  font-weight: 700;
  font-size: 18px;
  border-radius: 8px;
  padding: 12px 0;
}
.highlights-tabs .nav-link.active {
  background: #000;
  color: #fff;
}
.highlights-note {
  background: #efefef;
  border-radius: 8px;
  text-align: center;
  padding: 14px;
  margin-bottom: 20px;
  font-size: 16px;
  color: #333;
}
.empty-state {
  text-align: center;
  padding: 30px;
  color: #818181;
}
.hl-load-more {
  display: flex;
  justify-content: center;
  margin: 12px 0 4px;
}
.hl-load-more .hl-btn {
  min-width: 180px;
}
</style>
